import { IonRow, IonCol, IonSelect, IonSelectOption } from '@ionic/react'
import { useFormContext, Controller, useWatch } from 'react-hook-form'

import { useAccount, useUser } from '../../../contexts/authContext'
import { useAssociates } from '../../../hooks/queries/use-associate'
import { FormSection } from '../../../components/ui/form/form-section'
import { TextInput } from '../../../components/ui/form/input'
import { sendFrom } from '../../../automations/constants'

import type { SelectCustomEvent } from '@ionic/react'
import type { UpdateBroadcastInputs } from '../broadcast.schema'

type Props = { disabled?: boolean }

function BroadcastSendFrom({ disabled }: Props) {
  const { control, getValues, setValue } =
    useFormContext<UpdateBroadcastInputs>()
  const watchFromUserRule = useWatch({ control, name: 'fromUserRule' })

  const account = useAccount()
  const user = useUser()
  const userHasCustomEmail =
    user.hasSendGrid && account.sendgridDomains.length > 0

  const associatesQuery = useAssociates()
  const associates =
    associatesQuery.data?.pages
      .flatMap((d) => d.data)
      .filter((associate) =>
        userHasCustomEmail ? true : associate.objectId === user.objectId
      ) ?? []

  function resetSender() {
    setValue('fromUserId', null, { shouldDirty: true })
    setValue('fromEmailName', null, {
      shouldDirty: true,
    })
    setValue('fromEmailDomain', null, {
      shouldDirty: true,
    })
    setValue('fromName', null, { shouldDirty: true })
  }

  function updateSender({
    fromName,
    fromEmailName,
  }: {
    fromName: string
    fromEmailName?: string
  }) {
    setValue('fromName', fromName, { shouldDirty: true })
    setValue('fromEmailDomain', account.sendgridDomains[0], {
      shouldDirty: true,
    })
    setValue('fromEmailName', fromEmailName ?? null, { shouldDirty: true })
  }

  return (
    <FormSection>
      <h3>Send From</h3>
      <IonRow>
        <IonCol size="6">
          <Controller
            name="fromUserRule"
            control={control}
            render={({ field: { value, onChange } }) => (
              <IonSelect
                disabled={disabled}
                className="bg-ion-color-step-100 rounded"
                value={value}
                // w/o this the form value was missing fields when switching this select w/o selecting id
                // onIonBlur={recomputeFormValue}
                onIonChange={(e: SelectCustomEvent) => {
                  onChange(e.detail.value)
                  if (e.detail.value !== 'fromUser') {
                    resetSender()
                  } else {
                    setValue('fromUserId', user.objectId, {
                      shouldDirty: true,
                    })
                    updateSender({
                      fromName: user.displayName,
                      fromEmailName: user.twilioProxyEmail?.split('@')[0],
                    })
                  }
                }}
              >
                {sendFrom.map((from) => (
                  <IonSelectOption key={from.value} value={from.value}>
                    {from.label}
                  </IonSelectOption>
                ))}
              </IonSelect>
            )}
          />
        </IonCol>

        {watchFromUserRule === 'fromUser' ? (
          <IonCol size="6">
            <Controller
              name="fromUserId"
              control={control}
              render={({ field: { value, onChange } }) => (
                <IonSelect
                  style={{ '--placeholder-color': 'text-white' }}
                  disabled={disabled}
                  className="bg-ion-color-step-100 rounded"
                  value={value}
                  onIonChange={(e) => {
                    const associate = associates.find(
                      (a) => a.objectId === e.detail.value
                    )
                    if (!associate) return
                    updateSender({
                      fromName: associate.displayName,
                      fromEmailName: associate.twilioProxyEmail?.split('@')[0],
                    })
                    onChange(e.detail.value)
                  }}
                  placeholder="Select Sylist"
                >
                  {associates.map((associate) => (
                    <IonSelectOption
                      key={associate.objectId}
                      value={associate.objectId}
                    >
                      {associate.displayName}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              )}
            />
          </IonCol>
        ) : null}
      </IonRow>

      {watchFromUserRule === 'fromUser' &&
      (user.hasSendGrid ||
        getValues(['fromEmailName', 'fromEmailDomain', 'fromName']).some(
          (value) => value != null
        )) ? (
        <div className="space-y-2 py-4">
          <div className="flex-shrink-0 basis-2/5">
            <TextInput control={control} name="fromName" label="Sender Name" />
          </div>
          {account.sendgridDomains.length > 0 && user.hasSendGrid && (
            <div className="flex w-full items-end gap-x-2">
              <div className="flex-1">
                <TextInput
                  control={control}
                  name="fromEmailName"
                  className="lowercase"
                  label="Sender Email Address"
                />
              </div>
              <span className="text-lg">@</span>
              <Controller
                name="fromEmailDomain"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <IonSelect
                    style={{ '--placeholder-color': 'text-white' }}
                    className="bg-ion-color-step-100 rounded"
                    value={value}
                    onIonChange={(e) => onChange(e.detail.value)}
                  >
                    {account.sendgridDomains.map((d: string) => (
                      <IonSelectOption key={d}>{d}</IonSelectOption>
                    ))}
                  </IonSelect>
                )}
              />
            </div>
          )}
        </div>
      ) : null}
    </FormSection>
  )
}

export { BroadcastSendFrom }
